import { useState, useEffect } from 'react'
import { formatDistanceToNow } from 'date-fns'
import {
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Pause,
  Play,
  Trash2,
  Copy,
  Plus,
  Code,
} from 'lucide-react'
import { Button, Badge, Modal, Card, ConfirmModal } from '.'
import { cronApi } from '@/api/cron'
import { CreateCronMonitorModal } from './CreateCronMonitorModal'
import type { CronMonitor, HeartbeatURLResponse } from '@/types'
import toast from 'react-hot-toast'

interface CronMonitorsListProps {
  appId: string
}

export function CronMonitorsList({ appId }: CronMonitorsListProps) {
  const [monitors, setMonitors] = useState<CronMonitor[]>([])
  const [loading, setLoading] = useState(true)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [codeModalOpen, setCodeModalOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedMonitor, setSelectedMonitor] = useState<CronMonitor | null>(null)
  const [heartbeatInfo, setHeartbeatInfo] = useState<HeartbeatURLResponse | null>(null)
  const [activeTab, setActiveTab] = useState<'curl' | 'python' | 'javascript'>('curl')

  const loadMonitors = async () => {
    try {
      const response = await cronApi.listMonitors(appId)
      setMonitors(response.monitors)
    } catch (error) {
      console.error('Failed to load cron monitors:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadMonitors()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId])

  const handleCreated = (monitor: CronMonitor) => {
    setMonitors(prev => [monitor, ...prev])
  }

  const handleToggleActive = async (monitor: CronMonitor) => {
    try {
      const updated = await cronApi.updateMonitor(appId, monitor.id, {
        is_active: !monitor.is_active,
      })
      setMonitors(prev => prev.map(m => (m.id === updated.id ? updated : m)))
      toast.success(updated.is_active ? 'Monitor enabled' : 'Monitor disabled')
    } catch (error) {
      console.error('Failed to toggle monitor:', error)
      toast.error('Failed to update monitor')
    }
  }

  const handleDelete = async () => {
    if (!selectedMonitor) return
    try {
      await cronApi.deleteMonitor(appId, selectedMonitor.id)
      setMonitors(prev => prev.filter(m => m.id !== selectedMonitor.id))
      setDeleteDialogOpen(false)
      setSelectedMonitor(null)
      toast.success('Monitor deleted')
    } catch (error) {
      console.error('Failed to delete monitor:', error)
      toast.error('Failed to delete monitor')
    }
  }

  const handleShowCode = async (monitor: CronMonitor) => {
    try {
      const info = await cronApi.getHeartbeatUrl(appId, monitor.id)
      setHeartbeatInfo(info)
      setSelectedMonitor(monitor)
      setCodeModalOpen(true)
    } catch (error) {
      console.error('Failed to get heartbeat URL:', error)
      toast.error('Failed to get heartbeat URL')
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  const getStatusBadge = (monitor: CronMonitor) => {
    if (!monitor.is_active) {
      return (
        <Badge variant="warning">
          <Pause className="h-3 w-3 mr-1" />
          Disabled
        </Badge>
      )
    }

    switch (monitor.status) {
      case 'ok':
        return (
          <Badge variant="success">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            OK
          </Badge>
        )
      case 'missed':
        return (
          <Badge variant="error">
            <XCircle className="h-3 w-3 mr-1" />
            Missed
          </Badge>
        )
      default:
        return (
          <Badge variant="gray">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Unknown
          </Badge>
        )
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Cron Job Monitors</h3>
          <p className="text-sm text-slate-500">
            Monitor scheduled jobs by sending heartbeats after each run
          </p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Monitor
        </Button>
      </div>

      {monitors.length === 0 ? (
        <Card className="p-8 text-center">
          <Clock className="h-12 w-12 mx-auto text-slate-300 mb-4" />
          <h4 className="text-lg font-medium text-slate-900 mb-2">No cron monitors yet</h4>
          <p className="text-slate-500 mb-4">
            Create a monitor to track your scheduled jobs and get alerted when they miss their
            expected run time.
          </p>
          <Button onClick={() => setCreateModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create First Monitor
          </Button>
        </Card>
      ) : (
        <div className="border border-slate-200 rounded-lg divide-y divide-slate-200">
          {monitors.map(monitor => (
            <div
              key={monitor.id}
              className="p-4 flex items-center justify-between hover:bg-slate-50"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <h4 className="font-medium text-slate-900 truncate">{monitor.name}</h4>
                  {getStatusBadge(monitor)}
                </div>
                <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {monitor.schedule}
                  </span>
                  {monitor.last_heartbeat_at && (
                    <span>
                      Last heartbeat:{' '}
                      {formatDistanceToNow(new Date(monitor.last_heartbeat_at), {
                        addSuffix: true,
                      })}
                    </span>
                  )}
                  {!monitor.last_heartbeat_at && (
                    <span className="text-amber-500">Never received</span>
                  )}
                </div>
                {monitor.description && (
                  <p className="text-sm text-slate-500 mt-1 truncate">{monitor.description}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleShowCode(monitor)}>
                  <Code className="h-4 w-4 mr-1" />
                  Get Code
                </Button>
                <button
                  onClick={() => handleToggleActive(monitor)}
                  className={`p-2 rounded-lg transition-colors ${
                    monitor.is_active
                      ? 'text-green-600 hover:bg-green-50'
                      : 'text-slate-400 hover:bg-slate-100'
                  }`}
                  title={monitor.is_active ? 'Disable monitor' : 'Enable monitor'}
                >
                  {monitor.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => {
                    setSelectedMonitor(monitor)
                    setDeleteDialogOpen(true)
                  }}
                  className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                  title="Delete monitor"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateCronMonitorModal
        appId={appId}
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onCreated={handleCreated}
      />

      {/* Code Examples Modal */}
      <Modal
        isOpen={codeModalOpen}
        onClose={() => setCodeModalOpen(false)}
        title={`Heartbeat Integration - ${selectedMonitor?.name}`}
      >
        <p className="text-sm text-slate-500 -mt-2 mb-4">
          Use these code snippets to send heartbeats from your scheduled job
        </p>

        {heartbeatInfo && (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-700">Heartbeat URL</label>
                <button
                  onClick={() => copyToClipboard(heartbeatInfo.url)}
                  className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
                >
                  <Copy className="h-3 w-3" />
                  Copy
                </button>
              </div>
              <code className="block p-2 bg-slate-100 rounded text-sm break-all text-slate-700">
                {heartbeatInfo.url}
              </code>
            </div>

            <div>
              <div className="flex border-b border-slate-200">
                {(['curl', 'python', 'javascript'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === tab
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {tab === 'curl' ? 'cURL' : tab === 'python' ? 'Python' : 'JavaScript'}
                  </button>
                ))}
              </div>

              <div className="mt-4 relative">
                <button
                  onClick={() => {
                    const code =
                      activeTab === 'curl'
                        ? heartbeatInfo.curl_example
                        : activeTab === 'python'
                          ? heartbeatInfo.sdk_example_python
                          : heartbeatInfo.sdk_example_js
                    copyToClipboard(code)
                  }}
                  className="absolute right-2 top-2 p-1 rounded hover:bg-slate-200"
                >
                  <Copy className="h-4 w-4 text-slate-500" />
                </button>
                <pre className="p-4 bg-slate-100 rounded text-sm overflow-x-auto text-slate-700">
                  {activeTab === 'curl' && heartbeatInfo.curl_example}
                  {activeTab === 'python' && heartbeatInfo.sdk_example_python}
                  {activeTab === 'javascript' && heartbeatInfo.sdk_example_js}
                </pre>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Cron Monitor"
        message={`Are you sure you want to delete "${selectedMonitor?.name}"? This will also delete all run history. This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  )
}
